// Sound effects. The MVP ships no audio files, so sounds are synthesized with
// the Web Audio API (works offline, zero assets). Howler is wired as the path
// for real sound files: call registerSound(id, url) and that id plays the file
// instead of the synth. This keeps "Howler for SFX hooks" true while never
// blocking the build on missing art.

import { Howl } from 'howler';
import { bus } from '@core/events';

export type SoundId =
  | 'hit'
  | 'crit'
  | 'pickup'
  | 'build'
  | 'evolve'
  | 'card'
  | 'waveWin'
  | 'waveLose'
  | 'incoming'
  | 'milestone';

let audioCtx: AudioContext | null = null;
let muted = false;
const howls = new Map<SoundId, Howl>();

// Throttle high-frequency hit sounds so harvesting does not become a buzzsaw.
let lastHitSound = 0;

function ctx(): AudioContext {
  if (!audioCtx) {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    audioCtx = new Ctor();
  }
  return audioCtx;
}

/** A short tone, optionally gliding to a second frequency. */
function tone(
  freq: number,
  duration: number,
  type: OscillatorType,
  gain: number,
  slideTo?: number,
): void {
  const ac = ctx();
  const now = ac.currentTime;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, now);
  if (slideTo !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(1, slideTo), now + duration);
  g.gain.setValueAtTime(gain, now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  osc.connect(g);
  g.connect(ac.destination);
  osc.start(now);
  osc.stop(now + duration);
}

function arp(freqs: number[], step: number, type: OscillatorType, gain: number): void {
  freqs.forEach((f, i) => {
    const ac = ctx();
    const t = ac.currentTime + i * step;
    const osc = ac.createOscillator();
    const g = ac.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f, t);
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + step * 1.6);
    osc.connect(g);
    g.connect(ac.destination);
    osc.start(t);
    osc.stop(t + step * 1.8);
  });
}

const SYNTH: Record<SoundId, () => void> = {
  hit: () => tone(200, 0.05, 'square', 0.03, 150),
  crit: () => tone(640, 0.09, 'square', 0.05, 920),
  pickup: () => tone(520, 0.09, 'triangle', 0.045, 760),
  build: () => tone(150, 0.13, 'sawtooth', 0.045, 230),
  evolve: () => arp([440, 660, 880, 1100], 0.07, 'triangle', 0.05),
  card: () => arp([523, 784, 1046], 0.06, 'sine', 0.05),
  waveWin: () => arp([523, 659, 784, 1046], 0.06, 'square', 0.045),
  waveLose: () => tone(220, 0.5, 'sawtooth', 0.07, 55),
  incoming: () => tone(130, 0.22, 'square', 0.05, 90),
  milestone: () => arp([659, 988], 0.08, 'sine', 0.05),
};

export function play(id: SoundId): void {
  if (muted) return;
  const howl = howls.get(id);
  if (howl) {
    howl.play();
    return;
  }
  try {
    SYNTH[id]();
  } catch {
    /* audio not available; ignore */
  }
}

/** Register a real sound file for an id (Phase 4 audio pass uses this). */
export function registerSound(id: SoundId, src: string): void {
  howls.set(id, new Howl({ src: [src], volume: 0.6 }));
}

export function setMuted(m: boolean): void {
  muted = m;
}

export function isMuted(): boolean {
  return muted;
}

/** Wires sound cues to gameplay events. Audio context resumes on first input
 *  (browser autoplay policy). */
export function initAudio(initialMuted: boolean): void {
  muted = initialMuted;

  const resume = (): void => {
    if (audioCtx && audioCtx.state === 'suspended') void audioCtx.resume();
  };
  window.addEventListener('pointerdown', resume);
  window.addEventListener('keydown', resume);

  bus.on('harvest:hit', ({ crit }) => {
    if (crit) {
      play('crit');
      return;
    }
    const now = performance.now();
    if (now - lastHitSound > 70) {
      lastHitSound = now;
      play('hit');
    }
  });
  bus.on('harvest:drop', () => play('pickup'));
  bus.on('building:built', () => play('build'));
  bus.on('settlement:evolve', () => play('evolve'));
  bus.on('card:picked', () => play('card'));
  bus.on('restart', () => play('card'));
  bus.on('wave:incoming', () => play('incoming'));
  bus.on('wave:result', ({ win }) => play(win ? 'waveWin' : 'waveLose'));
  bus.on('tech:bought', ({ isBuff }) => play(isBuff ? 'evolve' : 'build'));
  bus.on('artifact:dropped', () => play('evolve'));
  bus.on('artifact:activated', () => play('waveWin'));
  bus.on('conquer:available', () => play('evolve'));
  bus.on('prestige', () => play('evolve'));
  bus.on('milestone', () => play('milestone'));
}
